import { useEffect, useMemo, useState } from 'react';

import { useDisclosure } from '@chakra-ui/react';

import { isDefined, isNotDefined } from '@enterprise/core';

import type { NthCalculatorGridContext } from '@components/nth-calculator/types/nth-calculator-grid-context.type';
import type { NthCalculatorRow } from '@components/nth-calculator/types/nth-calculator-row.type';

import type { UseDropTwentileCellConfigurationResult } from './types/drop-twentile-cell-configuration.type';

export const useDropTwentileCellConfiguration = ({
  context,
  data,
}: {
  context: NthCalculatorGridContext;
  data: NthCalculatorRow | undefined;
}): UseDropTwentileCellConfigurationResult => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [localSelected, setLocalSelected] = useState<number[]>(data?.selectedTwentileValues ?? []);

  const options = useMemo(() => {
    if (isNotDefined(data)) {
      return [];
    }

    return data.twentiles
      .filter(({ twentile }) => {
        return twentile > 0 && twentile < 11;
      })
      .map((twentile) => {
        return twentile;
      });
  }, [data]);

  const displayLabels = options
    .filter(({ twentile }) => {
      return (data?.selectedTwentileValues ?? []).includes(twentile);
    })
    .map(({ twentile }) => {
      return `${twentile}`;
    })
    .join(', ');

  const handleClear = () => {
    setLocalSelected([]);

    if (isNotDefined(data)) {
      return;
    }

    context.handleRowUpdate(data.mailingListId, { nthValue: null, selectedTwentileValues: [] });
  };

  const handleOpen = () => {
    setLocalSelected(data?.selectedTwentileValues ?? []);
    onOpen();
  };

  const handleClose = () => {
    if (isNotDefined(data)) {
      onClose();

      return;
    }

    context.handleRowUpdate(data.mailingListId, {
      nthValue: null,
      selectedTwentileValues: localSelected,
    });
    onClose();
  };

  const allValues = options.reduce<number[]>((acc, opt) => {
    if (isDefined(opt.twentile)) {
      acc.push(opt.twentile);
    }

    return acc;
  }, []);

  const isAllSelected =
    allValues.length > 0 &&
    allValues.every((val) => {
      return localSelected.includes(val);
    });

  const handleSelectAll = () => {
    setLocalSelected(isAllSelected ? [] : allValues);
  };

  const handleOptionChange = (checked: boolean, value?: number) => {
    if (isNotDefined(value)) {
      return;
    }

    setLocalSelected((prev) => {
      if (checked) {
        return [...prev, value];
      }

      return prev.filter((selected) => {
        return selected !== value;
      });
    });
  };

  useEffect(() => {
    setLocalSelected(data?.selectedTwentileValues ?? []);
  }, [data?.selectedTwentileValues]);

  return {
    displayLabels,
    handleClear,
    handleClose,
    handleOpen,
    handleOptionChange,
    handleSelectAll,
    isAllSelected,
    isOpen,
    localSelected,
    options,
  };
};
